import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow } from '@xyflow/react';
import EdgePopover from '../nodes/EdgePopover';
import { useBalancedPortValue, useIsBalanced } from '@/stores/caseStore';
import BalancedValues from './BalancedValues';
import PropertiesTooltip from './PropertiesTooltip';
import { useBalancedProperties } from '@/stores/caseStore';
import useToggle from '@/hooks/useToggle';
import { pathwayActions } from '@/stores/pathwayStore';
import { getControlPointPath } from '@/utils/edgePathUtils';

const PathwayEdge = props => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd,
    sourceHandleId,
    targetHandleId,
    label,
    source,
    data = {},
  } = props;
  const { name, anchorable, unit, typeName, controlPoint } = data;

  const reactFlow = useReactFlow();
  const [isDragging, setIsDragging] = useState(false);
  const dragStartedAt = useRef(null);
  const hasDragged = useRef(false);
  const suppressNextToggle = useRef(false);
  const balancedValues = useBalancedPortValue(source, 'output', name);
  const properties = useBalancedProperties(source, name);
  const isBalanced = useIsBalanced();
  const showBalancedValues = isBalanced && balancedValues;
  const { isOpen: isPopoverOpen, toggle: togglePopover, close: closePopover } = useToggle(false);

  const [defaultEdgePath, defaultLabelX, defaultLabelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const labelPosition = useMemo(
    () => controlPoint || { x: defaultLabelX, y: defaultLabelY },
    [controlPoint, defaultLabelX, defaultLabelY],
  );

  const edgePath = controlPoint
    ? getControlPointPath({ sourceX, sourceY, targetX, targetY, controlPoint })
    : defaultEdgePath;

  const edgeStyle = {
    transform: `translate(-50%, -50%) translate(${labelPosition.x}px,${labelPosition.y}px)`,
    pointerEvents: 'all',
  };

  const updateControlPointFromPointer = useCallback(
    event => {
      pathwayActions.updateEdgeControlPoint(
        id,
        reactFlow.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        }),
      );
    },
    [id, reactFlow],
  );

  const handlePointerDown = event => {
    if (event.button !== 0) return;

    event.stopPropagation();
    dragStartedAt.current = { x: event.clientX, y: event.clientY };
    hasDragged.current = false;
    setIsDragging(true);
  };

  const handlePopoverToggle = event => {
    if (suppressNextToggle.current) {
      event?.preventDefault();
      event?.stopPropagation();
      return;
    }

    if (!event) {
      togglePopover();
      return;
    }

    const distance = dragStartedAt.current
      ? Math.hypot(event.clientX - dragStartedAt.current.x, event.clientY - dragStartedAt.current.y)
      : 0;

    if (distance > 4) {
      event.preventDefault();
      event.stopPropagation();
      dragStartedAt.current = null;
      return;
    }

    togglePopover();
  };

  useEffect(() => {
    if (!isDragging) return undefined;

    const handlePointerMove = event => {
      if (dragStartedAt.current) {
        const distance = Math.hypot(event.clientX - dragStartedAt.current.x, event.clientY - dragStartedAt.current.y);

        if (distance <= 4 && !hasDragged.current) return;
      }

      event.preventDefault();
      hasDragged.current = true;
      updateControlPointFromPointer(event);
    };

    const handlePointerUp = event => {
      if (hasDragged.current) {
        event.preventDefault();
        suppressNextToggle.current = true;
        updateControlPointFromPointer(event);
        pathwayActions.saveEdgeControlPoint(id);
        window.setTimeout(() => {
          suppressNextToggle.current = false;
          dragStartedAt.current = null;
        }, 0);
      } else {
        dragStartedAt.current = null;
      }

      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp, { once: true });

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [id, isDragging, updateControlPointFromPointer]);

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          // @ts-ignore - pointerEvents is valid CSS property
          style={edgeStyle}
          onPointerDown={handlePointerDown}
          className={`z-1000 bg-base-100 p-2 nodrag nopan absolute rounded border border-gray-300 hover:bg-gray-300 select-none ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
        >
          <PropertiesTooltip properties={properties} label={label} isHidden={isPopoverOpen}>
            <EdgePopover
              anchorable={anchorable}
              name={name}
              typeName={typeName}
              label={label}
              edgeId={id}
              sourceId={source}
              sourceHandle={sourceHandleId}
              targetHandle={targetHandleId}
              unit={unit}
              isOpen={isPopoverOpen}
              onToggle={handlePopoverToggle}
              onClose={closePopover}
            >
              <div className="text-center font-semibold p-2">{label}</div>
              {showBalancedValues && <BalancedValues className="text-center" values={balancedValues} />}
            </EdgePopover>
          </PropertiesTooltip>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

PathwayEdge.propTypes = {
  id: PropTypes.string,
  sourceX: PropTypes.number,
  sourceY: PropTypes.number,
  targetX: PropTypes.number,
  targetY: PropTypes.number,
  sourcePosition: PropTypes.string,
  targetPosition: PropTypes.string,
  style: PropTypes.object,
  markerEnd: PropTypes.string,
  sourceHandleId: PropTypes.string,
  targetHandleId: PropTypes.string,
  label: PropTypes.string,
  source: PropTypes.string,
  data: PropTypes.object,
};

export default PathwayEdge;
